import { FC, useEffect, useState, useRef } from 'react';
import { IconCheck, IconX } from '@tabler/icons-react';
import { PasswordInput, PasswordInputProps } from '@mantine/core';

import { useFormComponentContext } from 'context/FormContext';
import useEditStyles from 'hooks/useEditStyles';

export const MINIMUM_PASSWORD_LENGTH = 8;

interface FormPasswordProps extends Omit<PasswordInputProps, 'onChange'> {
  name: string;
  label?: string;
  error?: string;
  value?: string;
  required?: boolean;
  showEdit?: boolean;
  placeholder?: string;
  description?: string;
  onChange?: (value: string) => void;
  onUpdate?: (value: string) => void;
}

interface FormPasswordRequirementProps {
  label: string;
  meets: boolean;
}

interface FormPasswordCheckProps {
  value: string;
  isValid: boolean;
  validatePassword: (isValid: boolean) => void;
}

const defaultPasswordRequirements = [
  { regex: /[a-z]/, label: 'Lowercase letter' },
  { regex: /[$&+,:;=?@#|'<>.^*()%!-]/, label: 'Special character' },
  { regex: /[0-9]/, label: 'Number' },
  { regex: /[A-Z]/, label: 'Uppercase letter' },
  {
    regex: new RegExp(`^.{${MINIMUM_PASSWORD_LENGTH},}$`),
    label: `Min ${MINIMUM_PASSWORD_LENGTH} characters`,
  },
];

const PasswordRequirement: FC<FormPasswordRequirementProps> = ({
  meets,
  label,
}) => {
  return (
    <div
      className={`flex items-center gap-2 min-w-0 ${meets ? '' : 'text-red-500'}`}
      style={meets ? { color: 'var(--green-color)' } : {}}
    >
      <span className="inline-block rounded-full flex-shrink-0">
        {meets ? (
          <IconCheck size={14} stroke={5.5} />
        ) : (
          <IconX size={14} stroke={5.5} />
        )}
      </span>
      <span className="text-sm custom-text-color opacity-70 truncate font-medium">{label}</span>
    </div>
  );
};

const FormPassword: FC<FormPasswordProps> = ({
  name,
  label,
  placeholder,
  description,
  required = false,
  showEdit = false,
  ...props
}) => {
  const form = useFormComponentContext();
  const { editedInputStyles } = useEditStyles({ name, showEdit });
  const { onUpdate, value, ...otherProps } = props;

  return (
    <div className="min-h-[96px]">
      <PasswordInput
        label={label}
        required={required}
        value={value || ''}
        placeholder={placeholder}
        description={description}
        styles={{ 
          ...editedInputStyles,
          ...props.styles,
          error: { marginTop: '0.25rem' },
          root: { marginBottom: 0 },
          input: { minHeight: '40px' },
          label: { marginBottom: '0.25rem' }
        }}
        {...form.getInputProps(name)}
        {...otherProps}
        onChange={(e) => {
          onUpdate?.(e.target.value);
          form.getInputProps(name).onChange(e.target.value);
        }}
      />
    </div>
  );
};

const PasswordCheck: FC<FormPasswordCheckProps> = ({
  value,
  isValid,
  validatePassword,
}) => {
  const [checkValue, setCheckValue] = useState(value);
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const unmountTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const showTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setCheckValue(value);
  }, [value]);

  useEffect(() => {
    const invalidCount = defaultPasswordRequirements.reduce(
      (acc: number, requirement: { regex: RegExp; label: string }) => {
        const meets = requirement.regex.test(value || '');
        return meets ? acc : acc + 1;
      },
      0
    );

    validatePassword(invalidCount === 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkValue]);

  // Clear all pending timeouts
  const clearAllTimeouts = () => {
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    if (unmountTimeoutRef.current) clearTimeout(unmountTimeoutRef.current);
    if (showTimeoutRef.current) clearTimeout(showTimeoutRef.current);
  };

  // Handle visibility animations
  useEffect(() => {
    clearAllTimeouts(); // Clear any pending timeouts first

    if (!value) {
      // No password - hide immediately
      setIsVisible(false);
      unmountTimeoutRef.current = setTimeout(() => setShouldRender(false), 300);
    } else if (value && !isValid) {
      // Has password and invalid - show with animation
      setShouldRender(true);
      showTimeoutRef.current = setTimeout(() => setIsVisible(true), 10); // Small delay for smooth entrance
    } else if (value && isValid) {
      // Has password and valid - hide with delay
      hideTimeoutRef.current = setTimeout(() => {
        setIsVisible(false);
        unmountTimeoutRef.current = setTimeout(() => setShouldRender(false), 300); // Wait for slide out animation
      }, 1000); // 1 second delay before starting hide animation
    }

    // Cleanup function
    return () => {
      clearAllTimeouts();
    };
  }, [value, isValid]);

  const checkList = defaultPasswordRequirements.map((requirement, index) => {
    const meets = requirement.regex.test(checkValue || '');
    return (
      <PasswordRequirement
        key={index}
        label={requirement.label}
        meets={meets}
      />
    );
  });

  if (!shouldRender) return null;

  return (
    <div 
      className={`grid grid-cols-2 gap-2 justify-between mb-6 transition-all duration-300 ease-in-out overflow-hidden ${
        isVisible 
          ? 'opacity-100 max-h-40 transform translate-y-0' 
          : 'opacity-0 max-h-0 transform -translate-y-2'
      }`}
    >
      {checkList}
    </div>
  );
};

export default FormPassword;
export { PasswordCheck };
